import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export type SalesPromptType = 'executive_summary' | 'scope_items' | 'product_assignment';

export interface SalesPrompt {
  id: string;
  brand_id: string;
  user_id: string;
  prompt_type: SalesPromptType;
  prompt_title: string;
  prompt_content: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export const useSalesPrompts = (brandId: string | null, promptType?: SalesPromptType) => {
  const queryClient = useQueryClient();

  const { data: prompts, isLoading } = useQuery({
    queryKey: ['sales-prompts', brandId, promptType],
    queryFn: async () => {
      if (!brandId) return [];
      let query = supabase
        .from('os_sales_prompts')
        .select('*')
        .eq('brand_id', brandId)
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });
      
      if (promptType) {
        query = query.eq('prompt_type', promptType);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      return data as SalesPrompt[];
    },
    enabled: !!brandId,
  });

  const createPrompt = useMutation({
    mutationFn: async (newPrompt: any) => {
      const { data, error } = await supabase
        .from('os_sales_prompts')
        .insert([newPrompt])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-prompts'] });
      toast({ title: 'Prompt created successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error creating prompt', description: error.message, variant: 'destructive' });
    },
  });

  const updatePrompt = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<SalesPrompt> }) => {
      const { data, error } = await supabase
        .from('os_sales_prompts')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-prompts'] });
      toast({ title: 'Prompt updated successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error updating prompt', description: error.message, variant: 'destructive' });
    },
  });

  const deletePrompt = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('os_sales_prompts')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-prompts'] });
      toast({ title: 'Prompt deleted successfully' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error deleting prompt', description: error.message, variant: 'destructive' });
    },
  });

  return {
    prompts,
    isLoading,
    createPrompt,
    updatePrompt,
    deletePrompt,
  };
};
